import { Link, useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { useNoshStore } from '../../lib/store';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import { Tilt } from '../ui/tilt';
import { motion } from 'framer-motion';
import { daysUntilExpiry } from '../../lib/utils';
import { useState, useEffect } from 'react';
import { ThemeLogo } from '../ui/theme-logo';
import MonthlyUsageCard from '../ui/MonthlyUsageCard';
import { 
  Home, 
  ShoppingCart, 
  Book, 
  CalendarDays, 
  CupSoda, 
  Settings,
  RefrigeratorIcon,
  LogOut,
  Search
} from 'lucide-react';

const Sidebar = () => {
  const location = useLocation();
  const { user, inventory, loading } = useNoshStore();
  const [userEmail, setUserEmail] = useState<string>('');

  // Get authenticated user's email
  useEffect(() => {
    const getAuthUser = async () => {
      try {
        const { supabase } = await import('../../lib/supabase');
        const { data: { user: authUser } } = await supabase.auth.getUser();
        if (authUser?.email) {
          setUserEmail(authUser.email);
        }
      } catch (error) {
        console.error('Error getting auth user:', error);
      }
    };

    getAuthUser();
  }, []);

  // Handle logout function
  const handleLogout = async () => {
    try {
      const { handleLogoutRedirect } = await import('../../lib/logout-utils');
      await handleLogoutRedirect();
    } catch (error) {
      console.error('Error logging out:', error);
      // Fallback: redirect using utility
      const { getLogoutRedirectUrl } = await import('../../lib/logout-utils');
      window.location.href = getLogoutRedirectUrl();
    }
  };

  // Get display name (user name or email prefix)
  const getDisplayName = () => {
    if (user.name && user.name !== 'User') {
      return user.name.split(' ')[0]; // First name only
    }
    
    // Fall back to email prefix
    if (userEmail) {
      return userEmail.split('@')[0];
    }
    
    return 'User';
  };

  // Calculate food-relevant statistics
  const getInventoryStats = () => {
    const totalItems = inventory.length;
    const expiringItems = inventory.filter(item => {
      const days = daysUntilExpiry(item.expiryDate);
      return days >= 0 && days <= 3; // Expiring in 3 days or less
    }).length;
    const freshItems = inventory.filter(item => {
      const days = daysUntilExpiry(item.expiryDate);
      return days >= 7; // Fresh items with 7+ days
    }).length;

    return { totalItems, expiringItems, freshItems };
  };

  const { totalItems, expiringItems, freshItems } = getInventoryStats();

  const navItems = [
    { 
      icon: <Home size={20} />, 
      label: 'Dashboard', 
      path: '/dashboard', 
      active: location.pathname === '/dashboard' 
    },
    { 
      icon: <RefrigeratorIcon size={20} />, 
      label: 'Inventory', 
      path: '/inventory',
      active: location.pathname === '/inventory'
    },
    { 
      icon: <Book size={20} />, 
      label: 'Recipes', 
      path: '/recipes',
      active: location.pathname === '/recipes'
    },
    { 
      icon: <CupSoda size={20} />, 
      label: 'Meal Tracker', 
      path: '/tracker',
      active: location.pathname === '/tracker'
    },
    { 
      icon: <CalendarDays size={20} />, 
      label: 'Meal Planner', 
      path: '/planner',
      active: location.pathname === '/planner'
    },
    { 
      icon: <ShoppingCart size={20} />, 
      label: 'Shopping List', 
      path: '/shopping',
      active: location.pathname === '/shopping'
    }
  ];

  return (
    <div className="w-60 flex-shrink-0 bg-card h-screen sticky top-0 p-5 flex flex-col border-r border-border relative md:rounded-3xl md:shadow-lg md:m-4 md:h-[calc(100vh-2rem)]">
      {/* Logo positioned at top-right corner - outside tilt effect */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.5 }}
        className="absolute top-5 right-5 z-30"
      >
        <Link to="/" className="flex items-center justify-center">
          <ThemeLogo className="h-10" />
        </Link>
      </motion.div>

      {/* Colorful Animated User Section */}
      <Tilt
        rotationFactor={8}
        isRevese
        springOptions={{
          stiffness: 26.7,
          damping: 4.1,
          mass: 0.2,
        }}
        className="relative overflow-visible rounded-2xl mb-6 bg-white shadow-lg"
        style={{ 
          marginTop: '60px',
          transformOrigin: 'center center',
        }}
      >
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          {/* Character Avatar - positioned to peek out of the top */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="absolute -top-10 left-4 z-20"
          >
            <div className="h-24 w-24 flex items-center justify-center">
              {loading.user ? (
                // Show loading spinner while profile is loading
                <div className="h-24 w-24 rounded-full bg-black/10 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-black/30 border-t-black"></div>
                </div>
              ) : (
                <img 
                  src={user.avatar} 
                  alt={user.name}
                  className="h-24 w-24 object-contain"
                />
              )}
            </div>
          </motion.div>
          
          {/* Header Content */}
          <div className="relative z-10 p-4 pt-16">
            {/* Welcome Text */}
            <div className="mb-4">
              <motion.h2 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.6 }}
                className="text-black text-lg font-heading font-bold leading-tight"
              >
                What's cooking, {getDisplayName()}? 😋
              </motion.h2>
              
            </div>
            
            {/* Stats Bar */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.9, duration: 0.5 }}
              className="flex items-center justify-between bg-gray-200 rounded-xl p-2.5"
            >
              <div className="text-center flex-1">
                <p className="text-black text-sm font-bold">{totalItems}</p>
                <p className="text-black/80 text-xs font-medium">Total</p>
              </div>
              <div className="w-px h-6 bg-black/30"></div>
              <div className="text-center flex-1">
                <p className="text-black text-sm font-bold">{expiringItems}</p>
                <p className="text-black/80 text-xs font-medium">Expiring</p>
              </div>
              <div className="w-px h-6 bg-black/30"></div>
              <div className="text-center flex-1">
                <p className="text-black text-sm font-bold">{freshItems}</p>
                <p className="text-black/80 text-xs font-medium">Fresh</p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </Tilt>

      <nav className="flex-1 overflow-y-auto scrollbar-hide">
        <ul className="space-y-2">
          {navItems.map((item) => (
            <li key={item.path}>
              <Link
                to={item.path}
                className={cn(
                  "flex items-center gap-3 px-4 py-3 rounded-xl transition-colors relative overflow-hidden",
                  item.active
                    ? "bg-primary text-white"
                    : "text-text-secondary hover:bg-muted"
                )}
              >
                {item.icon}
                <span>{item.label}</span>
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      {/* Monthly Usage Section */}
      <div className="mt-auto">
        <MonthlyUsageCard />
      </div>

      <div className="pt-6 border-t border-border">
        <div className="flex items-center gap-2">
          <Link
            to="/profile"
            className={cn(
              "flex items-center gap-3 px-4 py-3 rounded-xl transition-colors flex-1",
              location.pathname === '/profile'
                ? "bg-primary text-white"
                : "text-text-secondary hover:bg-muted"
            )}
          >
            <Settings size={20} />
            <span>Profile</span>
          </Link>
          <Button
            variant="ghost"
            size="icon"
            className="h-11 w-11 rounded-xl bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-700"
            onClick={handleLogout}
            title="Logout"
          >
            <LogOut size={20} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;